import { useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import { toast } from 'sonner'

// All available models
const ALL_MODELS = [
  // User's preferred models (default selection)
  { id: 'nvidia/nemotron-nano-12b-v2-vl:free', name: 'NVIDIA Nemotron Nano 12B', category: 'free' },
  { id: 'anthropic/claude-haiku-4.5', name: 'Claude Haiku 4.5', category: 'premium' },
  { id: 'google/gemini-2.5-flash-preview-09-2025', name: 'Gemini 2.5 Flash Preview', category: 'premium' },
  { id: 'x-ai/grok-4-fast', name: 'Grok 4 Fast', category: 'premium' },
  { id: 'openai/gpt-5-nano', name: 'GPT-5 Nano', category: 'premium' },
  { id: 'google/gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite', category: 'premium' },
  { id: 'mistralai/mistral-small-3.2-24b-instruct:free', name: 'Mistral Small 3.2 24B', category: 'free' },
  { id: 'openai/gpt-4.1-mini', name: 'GPT-4.1 Mini', category: 'premium' },
  { id: 'openai/gpt-4.1-nano', name: 'GPT-4.1 Nano', category: 'premium' },
  { id: 'meta-llama/llama-4-maverick:free', name: 'Llama 4 Maverick', category: 'free' },
  
  // Additional models
  { id: 'openai/gpt-4o', name: 'GPT-4o', category: 'premium' },
  { id: 'openai/gpt-4o-mini', name: 'GPT-4o Mini', category: 'premium' },
  { id: 'anthropic/claude-3.5-sonnet', name: 'Claude 3.5 Sonnet', category: 'premium' },
  { id: 'google/gemini-2.0-flash-exp:free', name: 'Gemini 2.0 Flash Exp', category: 'free' },
  { id: 'meta-llama/llama-3.3-8b-instruct:free', name: 'Llama 3.3 8B', category: 'free' },
  { id: 'mistralai/mistral-7b-instruct:free', name: 'Mistral 7B', category: 'free' },
  { id: 'minimax/minimax-m2:free', name: 'MiniMax M2', category: 'free' },
  { id: 'deepseek/deepseek-chat-v3-0324:free', name: 'DeepSeek Chat V3', category: 'free' }
]

// Default selected models (first 6 from preferred list)
const DEFAULT_MODELS = [
  'nvidia/nemotron-nano-12b-v2-vl:free',
  'anthropic/claude-haiku-4.5',
  'google/gemini-2.5-flash-preview-09-2025',
  'x-ai/grok-4-fast',
  'openai/gpt-5-nano',
  'google/gemini-2.5-flash-lite'
]

const DEFAULT_BASELINE = {
  label: 'December 2025',
  month: 12,
  year: 2025,
  notes: 'Manual baseline derived from Wayzata Squirt B1 Gold calendar review',
  events: [
    {
      date: '2025-12-01',
      type: 'practice',
      time_from: '6:45 PM',
      duration_minutes: 45,
      location: 'PIC B - Skills',
      is_shared: true,
      shared_with: 'Squirt B1 Blue',
      description: 'Skills session with Squirt B1 Blue'
    },
    {
      date: '2025-12-02',
      type: 'practice',
      time_from: '5:00 PM',
      duration_minutes: 60,
      location: 'PIC B',
      is_shared: true,
      shared_with: 'Squirt A Gold'
    },
    {
      date: '2025-12-05',
      type: 'practice',
      time_from: '5:45 PM',
      duration_minutes: 60,
      location: 'Brooklyn Park Rink No. 2',
      is_shared: true,
      shared_with: 'Squirt B2 Yellow'
    },
    {
      date: '2025-12-09',
      type: 'practice',
      time_from: '7:30 PM',
      duration_minutes: 60,
      location: 'Brooklyn Park Rink No. 2',
      is_shared: true,
      shared_with: 'Squirt A Blue'
    },
    {
      date: '2025-12-04',
      type: 'practice',
      time_from: '6:50 PM',
      duration_minutes: 60,
      location: 'Velocity',
      is_shared: false,
      notes: 'Solo ice confirmed'
    },
    {
      date: '2025-12-13',
      type: 'practice',
      time_from: '7:45 AM',
      duration_minutes: 60,
      location: 'PIC C',
      is_shared: false,
      notes: 'Solo ice confirmed'
    },
    {
      date: '2025-12-14',
      type: 'practice',
      time_from: '4:45 PM',
      duration_minutes: 60,
      location: 'Brooklyn Park Rink No. 1',
      is_shared: false,
      notes: 'Solo ice confirmed'
    },
    {
      date: '2025-12-06',
      type: 'game',
      time_from: '4:30 PM',
      duration_minutes: 75,
      location: 'Thaler Rink',
      opponent: 'Mound Westonka SB1',
      labels: ['league game']
    },
    {
      date: '2025-12-08',
      type: 'game',
      time_from: '1:45 PM',
      duration_minutes: 60,
      location: 'Brooklyn Park Rink No. 1',
      opponent: 'TBD',
      labels: ['scrimmage game']
    },
    {
      date: '2025-12-10',
      type: 'game',
      time_from: '6:30 PM',
      duration_minutes: 60,
      location: 'PIC A',
      opponent: 'Squirt B1 Blue',
      labels: ['league game']
    },
    {
      date: '2025-12-12',
      type: 'game',
      time_from: '6:30 PM',
      duration_minutes: 60,
      location: 'PIC C',
      opponent: 'OMG SB1 Gold',
      labels: ['league game']
    },
    {
      date: '2025-12-20',
      type: 'game',
      time_from: '4:00 PM',
      duration_minutes: 75,
      location: 'PIC A',
      opponent: 'Mpls Squirt B1',
      labels: ['league game']
    }
  ]
}

function BaselineStat({ label, value, accent }) {
  return (
    <div className="bg-white border border-blue-100 rounded-lg p-4 shadow-sm">
      <div className="text-sm font-medium text-gray-600">{label}</div>
      <div className={`text-3xl font-bold ${accent}`}>{value}</div>
    </div>
  )
}

function BaselineDiffRow({ label, baseline, actual }) {
  if (baseline == null && actual == null) return null

  const safeBaseline = baseline ?? 0
  const safeActual = actual ?? 0
  const diff = safeActual - safeBaseline
  const diffLabel = diff === 0 ? 'Match' : diff > 0 ? `+${diff}` : `${diff}`
  const diffColor = diff === 0 ? 'text-green-600' : diff > 0 ? 'text-orange-600' : 'text-blue-600'

  return (
    <tr>
      <td className="py-1 text-left text-gray-600">{label}</td>
      <td className="py-1 text-center text-gray-800">{safeBaseline}</td>
      <td className="py-1 text-center text-gray-800">{safeActual}</td>
      <td className={`py-1 text-center font-medium ${diffColor}`}>{diffLabel}</td>
    </tr>
  )
}

function MetricRow({ label, metrics }) {
  const precision = metrics ? formatMetric(metrics.precision) : '—'
  const recall = metrics ? formatMetric(metrics.recall) : '—'
  const f1 = metrics ? formatMetric(metrics.f1) : '—'
  return (
    <tr>
      <td className="py-1 text-left text-gray-600">{label}</td>
      <td className="py-1 text-center text-gray-800">{precision}</td>
      <td className="py-1 text-center text-gray-800">{recall}</td>
      <td className="py-1 text-center text-gray-800">{f1}</td>
    </tr>
  )
}

function ParsedEventRow({ event, baselineSummary }) {
  const type = (event.event_type || event.type || '').toLowerCase()
  const date = extractDateFromEvent(event)
  const time = extractTimeFromEvent(event)
  const key = buildEventKey(event)
  const isGame = type.includes('game')
  const isPractice = type.includes('practice')
  const shared = isEventShared(event)
  const solo = isPractice && !shared
  let baselineMatch = null

  if (key && baselineSummary?.sets) {
    if (isGame) baselineMatch = baselineSummary.sets.games.has(key)
    else if (isPractice) {
      baselineMatch = shared ? baselineSummary.sets.shared.has(key) : baselineSummary.sets.solo.has(key)
    }
  }

  const matchBadge = baselineMatch == null
    ? null
    : baselineMatch
      ? (<span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px]">Baseline</span>)
      : (<span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px]">New</span>)

  return (
    <div className="bg-white border border-gray-200 rounded p-2 text-xs">
      <div className="flex items-center justify-between mb-1">
        <div className="font-medium text-gray-900">
          {type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Event'}
          {event.opponent && ` vs ${event.opponent}`}
        </div>
        <div className="flex items-center gap-2">
          {matchBadge}
          {solo && (
            <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-[10px]">Solo</span>
          )}
          {shared && (
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-[10px]">Shared</span>
          )}
          {isGame && (
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px]">Game</span>
          )}
        </div>
      </div>
      <div className="text-gray-600">
        {date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date'}
        {time && ` • ${time}`}
      </div>
      {event.location && (
        <div className="text-gray-500 mt-1">📍 {event.location}</div>
      )}
      {event.shared_with && (
        <div className="text-gray-500 mt-1">🤝 {event.shared_with}</div>
      )}
      {event.notes && (
        <div className="text-gray-500 mt-1">📝 {event.notes}</div>
      )}
    </div>
  )
}

function formatBaselineEventTitle(event) {
  const type = (event.type || '').toLowerCase()
  if (type === 'game') {
    return `Game vs ${event.opponent || 'TBD'}`
  }
  if (event.is_shared) {
    return `Practice w/ ${event.shared_with}`
  }
  return 'Practice (Solo)'
}

function computeBaselineSummary(baseline) {
  const counts = { practices: 0, shared: 0, solo: 0, games: 0 }
  const sets = {
    solo: new Set(),
    shared: new Set(),
    games: new Set(),
    practices: new Set()
  }

  const events = baseline?.events ?? []
  for (const event of events) {
    const key = buildEventKey(event)
    if (!key) continue

    const type = (event.type || '').toLowerCase()
    if (type === 'game') {
      counts.games += 1
      sets.games.add(key)
    } else if (type === 'practice') {
      counts.practices += 1
      sets.practices.add(key)
      if (event.is_shared) {
        counts.shared += 1
        sets.shared.add(key)
      } else {
        counts.solo += 1
        sets.solo.add(key)
      }
    }
  }

  return {
    counts,
    sets,
    events,
    month: baseline?.month,
    year: baseline?.year
  }
}

function summarizeModelAgainstBaseline(model, baselineSummary) {
  const events = Array.isArray(model?.all_events) ? model.all_events : []
  const practiceKeys = new Set()
  const sharedKeys = new Set()
  const soloKeys = new Set()
  const gameKeys = new Set()

  for (const event of events) {
    const key = buildEventKey(event)
    if (!key) continue

    const type = (event.event_type || event.type || '').toLowerCase()
    if (type.includes('game')) {
      gameKeys.add(key)
    } else if (type.includes('practice')) {
      practiceKeys.add(key)
      if (isEventShared(event)) {
        sharedKeys.add(key)
      } else {
        soloKeys.add(key)
      }
    }
  }

  const totals = {
    practices: practiceKeys.size,
    shared: sharedKeys.size,
    solo: soloKeys.size,
    games: gameKeys.size
  }

  const metrics = {
    solo: calcMetricsFromSets(soloKeys, baselineSummary.sets.solo),
    shared: calcMetricsFromSets(sharedKeys, baselineSummary.sets.shared),
    games: calcMetricsFromSets(gameKeys, baselineSummary.sets.games),
    practices: calcMetricsFromSets(practiceKeys, baselineSummary.sets.practices)
  }

  const diff = {
    practices: totals.practices - baselineSummary.counts.practices,
    shared: totals.shared - baselineSummary.counts.shared,
    solo: totals.solo - baselineSummary.counts.solo,
    games: totals.games - baselineSummary.counts.games
  }

  return { totals, metrics, diff, soloKeys }
}

function calcMetricsFromSets(predictedSet, baselineSet) {
  if (!baselineSet) {
    return { precision: 0, recall: 0, f1: 0 }
  }

  let tp = 0
  for (const key of predictedSet) {
    if (baselineSet.has(key)) tp += 1
  }

  const fp = predictedSet.size - tp
  const fn = baselineSet.size - tp
  return calcMetrics(tp, fp, fn)
}

function calcMetrics(tp, fp, fn) {
  const precision = tp + fp === 0 ? 0 : tp / (tp + fp)
  const recall = tp + fn === 0 ? 0 : tp / (tp + fn)
  const f1 = precision + recall === 0 ? 0 : (2 * precision * recall) / (precision + recall)
  return { precision, recall, f1 }
}

function convertCrossbarToBaseline(crossbarResult, fallbackBaseline) {
  if (!crossbarResult?.all_events) return fallbackBaseline

  const referenceYear = fallbackBaseline?.year ?? new Date().getFullYear()
  const referenceMonth = fallbackBaseline?.month ?? new Date().getMonth() + 1

  const convertedEvents = crossbarResult.all_events
    .map((event) => {
      const start = event.start_time ? new Date(event.start_time) : null
      if (!start) return null

      if (start.getMonth() + 1 !== referenceMonth || start.getFullYear() !== referenceYear) return null

      const base = {
        date: start.toISOString().slice(0, 10),
        time_from: start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        duration_minutes: event.duration_minutes ?? 60,
        location: event.location || '',
        type: event.event_type || 'practice'
      }

      if (base.type === 'game') {
        return {
          ...base,
          opponent: event.opponent || event.title?.replace(/^Game vs\s*/i, '') || ''
        }
      }

      return {
        ...base,
        type: 'practice',
        is_shared: Boolean(event.is_shared_ice),
        shared_with: event.shared_with || '',
        notes: !event.is_shared_ice ? 'Solo ice (Crossbar)' : undefined
      }
    })
    .filter(Boolean)

  return {
    label: fallbackBaseline?.label || `Crossbar Baseline ${referenceMonth}/${referenceYear}`,
    month: referenceMonth,
    year: referenceYear,
    events: convertedEvents
  }
}

function extractDateFromEvent(event) {
  if (event.date) {
    const parsed = new Date(event.date)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  if (event.start_time) {
    const parsed = new Date(event.start_time)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

function extractTimeFromEvent(event) {
  if (event.time_from) {
    return normalizeTimeTo24h(event.time_from)
  }
  if (event.start_time) {
    const parsed = new Date(event.start_time)
    if (Number.isNaN(parsed.getTime())) return null
    return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`
  }
  return null
}

function normalizeTimeTo24h(timeStr) {
  if (!timeStr) return null
  const match = timeStr.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i)
  if (!match) return null
  let hour = parseInt(match[1], 10)
  const minute = match[2]
  const meridian = (match[3] || '').toUpperCase()
  if (meridian === 'PM' && hour !== 12) hour += 12
  if (meridian === 'AM' && hour === 12) hour = 0
  return `${String(hour).padStart(2, '0')}:${minute}`
}

function buildEventKey(event) {
  const date = extractDateFromEvent(event)
  const time = extractTimeFromEvent(event)
  if (!date || !time) return null
  return `${date.toISOString().slice(0, 10)}|${time}`
}

function isEventShared(event) {
  if (event.is_shared === true || event.is_shared_ice === true) return true
  if (event.is_shared === false || event.is_shared_ice === false) return false
  const text = `${event.title || ''} ${event.description || ''}`.toLowerCase()
  if (/shared\s*w\//.test(text) || /skills\s*w\//.test(text)) return true
  if (/solo/.test(text)) return false
  return false
}

function formatMetric(value) {
  if (value == null) return '—'
  return `${Math.round(value * 100)}%`
}

export default function AIParserTestPage() {
  const [testUrl, setTestUrl] = useState('https://www.wayzatahockey.org/team/141011/calendar')
  const [teamName, setTeamName] = useState('Wayzata Squirt B1 Gold')
  const [targetMonth, setTargetMonth] = useState(DEFAULT_BASELINE.month)
  const [targetYear, setTargetYear] = useState(DEFAULT_BASELINE.year)
  const [promptStrategy, setPromptStrategy] = useState('detailed')
  const [selectedModels, setSelectedModels] = useState(DEFAULT_MODELS)
  const [includeCrossbar, setIncludeCrossbar] = useState(true)
  const [useRawHtml, setUseRawHtml] = useState(true)
  const [useRenderedHtml, setUseRenderedHtml] = useState(true)
  const [useScreenshot, setUseScreenshot] = useState(false)
  const [baseline, setBaseline] = useState(DEFAULT_BASELINE)
  const [baselineEditorOpen, setBaselineEditorOpen] = useState(false)
  const [baselineDraft, setBaselineDraft] = useState(JSON.stringify(DEFAULT_BASELINE, null, 2))
  const [baselineError, setBaselineError] = useState(null)
  const [testing, setTesting] = useState(false)
  const [results, setResults] = useState(null)
  const [latestCrossbarResult, setLatestCrossbarResult] = useState(null)
  const [expandedModels, setExpandedModels] = useState({})
  const [showModelSelector, setShowModelSelector] = useState(false)

  const baselineSummary = useMemo(() => computeBaselineSummary(baseline), [baseline])
  const modelSummaries = useMemo(() => {
    if (!results?.results) return {}
    const summaries = {}
    for (const model of results.results) {
      summaries[model.model] = summarizeModelAgainstBaseline(model, baselineSummary)
    }
    return summaries
  }, [results, baselineSummary])

  function toggleModel(modelId) {
    setSelectedModels(prev => {
      if (prev.includes(modelId)) {
        return prev.filter(id => id !== modelId)
      } else {
        if (prev.length >= 6) {
          toast.error('Maximum 6 models can be selected at once')
          return prev
        }
        return [...prev, modelId]
      }
    })
  }

  function openBaselineEditor() {
    setBaselineDraft(JSON.stringify(baseline, null, 2))
    setBaselineError(null)
    setBaselineEditorOpen(true)
  }

  function resetBaselineToDefault() {
    setBaseline(DEFAULT_BASELINE)
    setBaselineDraft(JSON.stringify(DEFAULT_BASELINE, null, 2))
    setBaselineError(null)
  }

  function applyBaselineDraft() {
    try {
      const parsed = JSON.parse(baselineDraft)
      if (!parsed || !Array.isArray(parsed.events)) {
        throw new Error('Baseline must include an events array')
      }
      setBaseline(parsed)
      setBaselineEditorOpen(false)
      setBaselineError(null)
      toast.success('Baseline updated for this session')
    } catch (error) {
      setBaselineError(error.message)
    }
  }

  function useCrossbarAsBaseline() {
    if (!latestCrossbarResult?.all_events) {
      toast.error('No Crossbar result available yet')
      return
    }

    const converted = convertCrossbarToBaseline(latestCrossbarResult, baseline)
    setBaseline(converted)
    setBaselineDraft(JSON.stringify(converted, null, 2))
    setBaselineError(null)
    toast.success('Baseline updated from Crossbar result (session only)')
  }

  async function runTest() {
    try {
      if (selectedModels.length === 0 && !includeCrossbar) {
        toast.error('Please select at least one model or enable Crossbar parser')
        return
      }

      setTesting(true)
      setResults(null)
      setExpandedModels({})

      const { data: { session } } = await supabase.auth.getSession()
      
      const totalTests = selectedModels.length + (includeCrossbar ? 1 : 0)
      toast.info(`Testing ${totalTests} parser${totalTests > 1 ? 's' : ''}... This may take 30-90 seconds`)

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-ai-parsers`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            test_url: testUrl,
            team_name: teamName,
            target_month: Number(targetMonth),
            target_year: Number(targetYear),
            prompt_strategy: promptStrategy,
            selected_models: selectedModels,
            include_crossbar: includeCrossbar,
            use_raw_html: useRawHtml,
            use_rendered_html: useRenderedHtml,
            use_screenshot: useScreenshot
          })
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Test failed')
      }

      setResults(result)
      const crossbar = result.results?.find((r) => r.parser_type === 'crossbar') || null
      setLatestCrossbarResult(crossbar)
      toast.success(`Test complete! ${result.models_succeeded}/${result.models_tested} parsers succeeded`)

    } catch (error) {
      console.error('Error running test:', error)
      toast.error(`Test failed: ${error.message}`)
    } finally {
      setTesting(false)
    }
  }

  function toggleModelExpansion(modelName) {
    setExpandedModels(prev => ({
      ...prev,
      [modelName]: !prev[modelName]
    }))
  }

  function getScoreColor(model) {
    if (!model.success) return 'bg-gray-100 text-gray-600'

    const summary = modelSummaries[model.model]
    if (!summary) return 'bg-gray-100 text-gray-600'

    const baselinePractices = baselineSummary.counts.shared + baselineSummary.counts.solo
    const diff = Math.abs(summary.totals.practices - baselinePractices)

    if (diff === 0) return 'bg-green-100 text-green-800'
    if (diff <= 1) return 'bg-yellow-100 text-yellow-800'
    if (diff <= 3) return 'bg-orange-100 text-orange-800'
    return 'bg-red-100 text-red-800'
  }

  function getAccuracyScore(model) {
    if (!model.success) return 'N/A'

    const summary = modelSummaries[model.model]
    if (!summary?.metrics) return '—'

    const soloF1 = summary.metrics.solo?.f1 ?? 0
    return `${Math.round(soloF1 * 100)}% Solo F1`
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={() => window.history.back()}
          className="text-blue-600 hover:text-blue-800 mb-4"
        >
          ← Back to Admin
        </button>
        <h1 className="text-3xl font-bold text-gray-900">AI Parser Model Testing</h1>
        <p className="text-gray-600 mt-2">
          Compare different AI models for HTML schedule extraction accuracy
        </p>
      </div>

      {/* Test Configuration */}
      <Card>
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Configuration</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Test URL (Calendar Page)
            </label>
            <input
              type="url"
              value={testUrl}
              onChange={(e) => setTestUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://www.wayzatahockey.org/team/141011/calendar"
            />
            <p className="text-sm text-gray-500 mt-1">
              Use Crossbar.org calendar view URLs for best results
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team Name (for prompts)
              </label>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Wayzata Squirt B1 Gold"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Month
              </label>
              <input
                type="number"
                min="1"
                max="12"
                value={targetMonth}
                onChange={(e) => setTargetMonth(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Year
              </label>
              <input
                type="number"
                min="2020"
                max="2035"
                value={targetYear}
                onChange={(e) => setTargetYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompting Strategy
            </label>
            <select
              value={promptStrategy}
              onChange={(e) => setPromptStrategy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="detailed">Detailed Instructions (default)</option>
              <option value="concise">Concise Rules</option>
              <option value="step_by_step">Step-by-Step Guide</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Different prompting approaches to test which works best
            </p>
          </div>

          {/* Model Selection */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Models to Test ({selectedModels.length}/6 selected)
              </label>
              <button
                onClick={() => setShowModelSelector(!showModelSelector)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showModelSelector ? 'Hide' : 'Select Models'}
              </button>
            </div>
            
            {/* Crossbar Parser Toggle */}
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                id="include-crossbar"
                checked={includeCrossbar}
                onChange={(e) => setIncludeCrossbar(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="include-crossbar" className="text-sm text-gray-700">
                Include Crossbar Parser (Baseline)
              </label>
            </div>

            {showModelSelector && (
              <div className="border border-gray-300 rounded-lg p-4 max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {ALL_MODELS.map((model) => (
                    <div key={model.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={model.id}
                        checked={selectedModels.includes(model.id)}
                        onChange={() => toggleModel(model.id)}
                        disabled={!selectedModels.includes(model.id) && selectedModels.length >= 6}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      />
                      <label 
                        htmlFor={model.id} 
                        className="text-sm text-gray-700 flex-1 cursor-pointer"
                      >
                        {model.name}
                      </label>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        model.category === 'free' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {model.category}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200 flex gap-2">
                  <button
                    onClick={() => setSelectedModels(DEFAULT_MODELS)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Reset to Defaults
                  </button>
                  <button
                    onClick={() => setSelectedModels([])}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            {!showModelSelector && selectedModels.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedModels.map(modelId => {
                  const model = ALL_MODELS.find(m => m.id === modelId)
                  return model ? (
                    <span key={modelId} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs flex items-center gap-1">
                      {model.name}
                      <button
                        onClick={() => toggleModel(modelId)}
                        className="text-gray-500 hover:text-red-600"
                      >
                        ×
                      </button>
                    </span>
                  ) : null
                })}
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Input Sources for Models
            </label>
            <div className="grid gap-3 md:grid-cols-3">
              <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300">
                <input
                  type="checkbox"
                  checked={useRawHtml}
                  onChange={(e) => setUseRawHtml(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Raw HTML (direct fetch)</div>
                  <div className="text-xs text-gray-500">
                    Original page source via fetch. Cheap but lacks JS-rendered content.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300">
                <input
                  type="checkbox"
                  checked={useRenderedHtml}
                  onChange={(e) => setUseRenderedHtml(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Rendered HTML (Browserless)</div>
                  <div className="text-xs text-gray-500">
                    Headless Chrome snapshot after clicking the month tab. Captures dynamic calendar content.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-blue-300">
                <input
                  type="checkbox"
                  checked={useScreenshot}
                  onChange={(e) => setUseScreenshot(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">Screenshot (Vision Models)</div>
                  <div className="text-xs text-gray-500">
                    Base64 screenshot of the rendered month grid. Use for OCR/vision-capable models.
                  </div>
                </div>
              </label>
            </div>

            {results?.input_previews && (
              <div className="mt-3 border border-dashed border-gray-300 rounded-lg p-3 text-xs text-gray-600 space-y-2 bg-gray-50">
                <div className="font-medium text-gray-800">Latest input previews (truncated):</div>
                {useRenderedHtml && results.input_previews?.rendered_html && (
                  <div>
                    <div className="uppercase tracking-wide text-[10px] text-gray-500">Rendered HTML</div>
                    <pre className="whitespace-pre-wrap bg-white border border-gray-200 rounded p-2 max-h-40 overflow-y-auto">
                      {results.input_previews.rendered_html.sample || '[Unavailable]'}
                    </pre>
                  </div>
                )}
                {useRawHtml && results.input_previews?.raw_html && (
                  <div>
                    <div className="uppercase tracking-wide text-[10px] text-gray-500">Raw HTML</div>
                    <pre className="whitespace-pre-wrap bg-white border border-gray-200 rounded p-2 max-h-40 overflow-y-auto">
                      {results.input_previews.raw_html.sample || '[Unavailable]'}
                    </pre>
                  </div>
                )}
                {useScreenshot && results.input_previews?.screenshot_base64 && (
                  <div>
                    <div className="uppercase tracking-wide text-[10px] text-gray-500">Screenshot (base64)</div>
                    <pre className="whitespace-pre-wrap bg-white border border-gray-200 rounded p-2 max-h-40 overflow-y-auto">
                      {results.input_previews.screenshot_base64.sample || '[Unavailable]'}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          <Button
            onClick={runTest}
            disabled={testing || !testUrl || (selectedModels.length === 0 && !includeCrossbar)}
            variant="primary"
            className="w-full"
          >
            {testing ? 'Testing Parsers...' : `Run Test (${selectedModels.length + (includeCrossbar ? 1 : 0)} parsers)`}
          </Button>

          {testing && (
            <div className="mt-4 text-center text-sm text-gray-600">
              <div className="animate-pulse">
                Testing {selectedModels.length + (includeCrossbar ? 1 : 0)} parser{selectedModels.length + (includeCrossbar ? 1 : 0) > 1 ? 's' : ''} in batches...
              </div>
              <div className="text-xs mt-1">This will take 30-90 seconds</div>
            </div>
          )}
        </div>
      </Card>

      <Card className="mt-6">
        <div className="p-6 bg-blue-50 border border-blue-100 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Manual Golden Baseline — {baseline.label}
              </h3>
              <p className="text-sm text-gray-600">
                Stored in session only. Edit as needed or sync from the latest Crossbar baseline run.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" className="text-sm" onClick={openBaselineEditor}>
                Edit Baseline JSON
              </Button>
              <Button
                variant="secondary"
                className="text-sm"
                onClick={resetBaselineToDefault}
              >
                Reset to Default
              </Button>
              <Button
                variant="secondary"
                className="text-sm"
                onClick={useCrossbarAsBaseline}
                disabled={!latestCrossbarResult}
              >
                Use Latest Crossbar Result
              </Button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <BaselineStat label="Total Practices" value={baselineSummary.counts.practices} accent="text-blue-700" />
            <BaselineStat label="Shared Practices" value={baselineSummary.counts.shared} accent="text-yellow-700" />
            <BaselineStat label="Solo Practices" value={baselineSummary.counts.solo} accent="text-green-700" />
            <BaselineStat label="Games" value={baselineSummary.counts.games} accent="text-purple-700" />
          </div>

          <details className="mt-4 text-sm">
            <summary className="cursor-pointer text-blue-700 hover:text-blue-900 font-medium">
              View baseline events ({baseline.events.length})
            </summary>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {baseline.events.map((event, idx) => (
                <div key={`${event.date}-${event.time_from}-${idx}`} className="bg-white border border-blue-100 rounded p-3 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-gray-900">{formatBaselineEventTitle(event)}</div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div>⏰ {event.time_from} ({event.duration_minutes}m)</div>
                    {event.location && <div>📍 {event.location}</div>}
                    {event.is_shared && (
                      <div>🤝 Shared w/ {event.shared_with}</div>
                    )}
                    {!event.is_shared && event.type === 'practice' && (
                      <div>✅ Solo ice</div>
                    )}
                    {event.opponent && <div>⚔️ vs. {event.opponent}</div>}
                    {event.notes && <div>📝 {event.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          </details>
        </div>
      </Card>

      {baselineEditorOpen && (
        <Card className="mt-4 border border-blue-200">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Edit Baseline JSON</h3>
            <p className="text-sm text-gray-600 mb-3">
              Update the manual baseline for this session. Expected shape: {"{ label, month, year, events: [] }"}. Only stored locally; reloading the page resets to defaults.
            </p>
            <textarea
              value={baselineDraft}
              onChange={(e) => setBaselineDraft(e.target.value)}
              className="w-full h-60 font-mono text-xs border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500"
            />
            {baselineError && (
              <div className="mt-2 text-sm text-red-600">{baselineError}</div>
            )}
            <div className="mt-4 flex gap-2">
              <Button variant="primary" onClick={applyBaselineDraft}>
                Save Baseline
              </Button>
              <Button variant="secondary" onClick={() => setBaselineEditorOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Test Results */}
      {results && (
        <>
          {/* Summary Stats */}
          <Card className="mt-6">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Test Summary
                {results.is_crossbar && (
                  <span className="ml-3 text-sm font-normal text-green-600">
                    ✓ Crossbar.org Site Detected
                  </span>
                )}
              </h2>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <div className="text-3xl font-bold text-blue-600">
                    {results.models_succeeded}/{results.models_tested}
                  </div>
                  <div className="text-sm text-gray-600">Parsers Succeeded</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-green-600">
                    {results.summary.avg_event_count}
                  </div>
                  <div className="text-sm text-gray-600">Avg Events</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600">
                    {results.summary.avg_shared_ice_detected} / {results.summary.avg_solo_ice_detected}
                  </div>
                  <div className="text-sm text-gray-600">Shared / Solo</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-600">
                    {results.summary.avg_december_solo}
                  </div>
                  <div className="text-sm text-gray-600">Avg Dec Solo</div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                <strong>Prompt Strategy:</strong> {results.prompt_strategy}
              </p>
            </div>
          </Card>

          {/* Model Results Table */}
          <Card className="mt-6">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Model Results</h2>
              
              <div className="space-y-3">
                {results.results.map((model, index) => {
                  const summary = modelSummaries[model.model]
                  const metrics = summary?.metrics
                  const diff = summary?.diff

                  return (
                  <div key={model.model} className="border border-gray-200 rounded-lg">
                    {/* Model Header Row */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleModelExpansion(model.model)}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="text-sm font-medium text-gray-500 w-8">#{index + 1}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-gray-900">{model.model}</div>
                            {model.parser_type === 'crossbar' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                Baseline
                              </span>
                            )}
                            {model.inputs_used && (
                              <div className="flex flex-wrap gap-1">
                                {model.inputs_used.rendered_html && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">Rendered</span>
                                )}
                                {model.inputs_used.raw_html && (
                                  <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-[10px]">Raw</span>
                                )}
                                {model.inputs_used.screenshot && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px]">Screenshot</span>
                                )}
                              </div>
                            )}
                          </div>
                          {model.error && (
                            <div className="text-xs text-red-600 mt-1">{model.error}</div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Status */}
                        <div className="text-center">
                          {model.success ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                              ✓ Success
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                              ✗ Failed
                            </span>
                          )}
                        </div>

                        {/* Events Count */}
                        <div className="text-center w-20">
                          <span className={`px-2 py-1 rounded font-medium text-sm ${getScoreColor(model)}`}>
                            {model.event_count}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">Events</div>
                        </div>

                        {/* Shared/Solo */}
                        <div className="text-center w-24">
                          <div className="font-medium text-sm text-gray-900">
                            {model.shared_ice_detected} / {model.solo_ice_detected}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Shared/Solo</div>
                        </div>

                        {/* Parse Time */}
                        <div className="text-center w-20">
                          <div className="font-medium text-sm text-gray-700">
                            {(model.parse_time_ms / 1000).toFixed(1)}s
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Time</div>
                        </div>

                        {/* Accuracy */}
                        <div className="text-center w-20">
                          <div className="font-medium text-sm text-gray-900">
                            {getAccuracyScore(model)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Accuracy</div>
                        </div>

                        {/* Expand Icon */}
                        <div className="w-6">
                          {expandedModels[model.model] ? (
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedModels[model.model] && model.success && model.all_events && (
                      <div className="border-t border-gray-200 p-4 bg-gray-50">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Diff vs Baseline</h4>
                            <table className="w-full text-xs">
                              <tbody className="text-gray-700">
                                <BaselineDiffRow label="Total Practices" baseline={baselineSummary.counts.practices} actual={summary?.totals?.practices} />
                                <BaselineDiffRow label="Shared" baseline={baselineSummary.counts.shared} actual={summary?.totals?.shared} />
                                <BaselineDiffRow label="Solo" baseline={baselineSummary.counts.solo} actual={summary?.totals?.solo} />
                                <BaselineDiffRow label="Games" baseline={baselineSummary.counts.games} actual={summary?.totals?.games} />
                              </tbody>
                            </table>
                          </div>

                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Precision / Recall / F1</h4>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left">Category</th>
                                  <th>Precision</th>
                                  <th>Recall</th>
                                  <th>F1</th>
                                </tr>
                              </thead>
                              <tbody className="text-gray-700">
                                <MetricRow label="Solo Practice" metrics={metrics?.solo} />
                                <MetricRow label="Shared Practice" metrics={metrics?.shared} />
                                <MetricRow label="Games" metrics={metrics?.games} />
                                <MetricRow label="All Practices" metrics={metrics?.practices} />
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <details className="mt-4">
                          <summary className="text-sm text-blue-600 cursor-pointer hover:text-blue-800">
                            Show parsed events ({model.all_events.length})
                          </summary>
                          <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
                            {model.all_events.map((event, idx) => (
                              <ParsedEventRow key={idx} event={event} baselineSummary={baselineSummary} />
                            ))}
                          </div>
                        </details>

                        {model.usage && (
                          <div className="mt-4 grid gap-3 md:grid-cols-3 text-xs text-gray-600">
                            <div className="bg-white border border-gray-200 rounded p-3">
                              <div className="font-semibold text-gray-900 text-sm">Prompt Length</div>
                              <div>{model.prompt_char_length?.toLocaleString() ?? '—'} chars</div>
                            </div>
                            <div className="bg-white border border-gray-200 rounded p-3">
                              <div className="font-semibold text-gray-900 text-sm">Tokens</div>
                              <div>{model.usage.total_tokens ?? '—'} total</div>
                              <div>Prompt: {model.usage.prompt_tokens ?? '—'} | Completion: {model.usage.completion_tokens ?? '—'}</div>
                            </div>
                            <div className="bg-white border border-gray-200 rounded p-3">
                              <div className="font-semibold text-gray-900 text-sm">Estimated Cost</div>
                              <div>${(model.estimated_cost_usd ?? 0).toFixed(4)}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )})}
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
